// Single product page interactivity: gallery thumbnails, description tabs,
// and the frequently-bought-together bundle (running total + add-to-cart).

import { openWithFragments } from './cart-drawer';

function ajaxUrl() {
    return (window.settings && window.settings.admin_ajax_url) || '/wp-admin/admin-ajax.php';
}

function nonce() {
    return (window.settings && window.settings.cart_nonce) || '';
}

function initGallery(page) {
    const main = page.querySelector('[data-product-gallery-main]');
    const thumbs = page.querySelectorAll('[data-product-gallery-thumb]');
    if (!main || !thumbs.length) return;

    thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
            const full = thumb.getAttribute('data-full');
            if (!full) return;
            main.setAttribute('src', full);
            thumbs.forEach((t) => t.classList.remove('product-page__gallery-thumb--active'));
            thumb.classList.add('product-page__gallery-thumb--active');
        });
    });
}

function initTabs(page) {
    const buttons = page.querySelectorAll('[data-tab]');
    const panels = page.querySelectorAll('[data-tab-panel]');
    buttons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const tab = btn.getAttribute('data-tab');
            buttons.forEach((b) => b.classList.remove('product-page__tab-btn--active'));
            panels.forEach((p) => p.classList.remove('product-page__tab-panel--active'));
            btn.classList.add('product-page__tab-btn--active');
            const panel = page.querySelector(`[data-tab-panel="${tab}"]`);
            if (panel) panel.classList.add('product-page__tab-panel--active');
        });
    });
}

function initBundle(page) {
    const toggles = page.querySelectorAll('[data-fbt-toggle]');
    const totalEl = page.querySelector('[data-fbt-total]');
    const addBtn = page.querySelector('[data-fbt-add]');
    if (!toggles.length || !totalEl) return;

    const basePrice = parseFloat((totalEl.textContent || '0').replace(/[^\d.]/g, '')) || 0;

    function recompute() {
        let sum = basePrice;
        toggles.forEach((t) => {
            if (t.checked) sum += parseFloat(t.getAttribute('data-price')) || 0;
        });
        totalEl.textContent = `£${sum.toFixed(2)}`;
    }

    toggles.forEach((t) => t.addEventListener('change', recompute));
    // Every toggle starts checked (see render-product-page.php) — without
    // this, the total shown on first paint was just the base product price
    // until the visitor touched a checkbox, ignoring the bundle items it
    // was already implicitly including.
    recompute();

    if (addBtn) initBundleAdd(page, addBtn, toggles);
}

// Every bundle checkbox already resolves to one exact product+variation
// server-side (see TDS\get_bundle_products() / resolve_bundle_candidate())
// — the only variant choice left is the main product's own, read straight
// off the real variations form so this can't add a flavour the customer
// didn't pick.
function initBundleAdd(page, addBtn, toggles) {
    addBtn.addEventListener('click', () => {
        const mainProductId = addBtn.getAttribute('data-fbt-main-product-id');
        const variationsForm = page.querySelector('.variations_form');
        let mainVariationId = '0';

        if (variationsForm) {
            const variationInput = variationsForm.querySelector('input[name="variation_id"]');
            mainVariationId = variationInput ? variationInput.value : '0';
            if (!mainVariationId || mainVariationId === '0') {
                alert('Please choose a flavour above before adding the bundle.');
                return;
            }
        }

        const items = [{ product_id: mainProductId, variation_id: mainVariationId }];
        toggles.forEach((t) => {
            if (!t.checked) return;
            const productId = t.getAttribute('data-fbt-product-id');
            if (!productId || productId === '0') return; // last-resort demo fallback row, not a real product
            items.push({ product_id: productId, variation_id: t.getAttribute('data-fbt-variation-id') || '0' });
        });

        addBtn.disabled = true;
        const body = new URLSearchParams({ action: 'fc_add_bundle_to_cart', nonce: nonce() });
        items.forEach((item, i) => {
            body.append(`items[${i}][product_id]`, item.product_id);
            body.append(`items[${i}][variation_id]`, item.variation_id);
        });

        fetch(ajaxUrl(), { method: 'POST', body, credentials: 'same-origin' })
            .then((r) => r.json())
            .then((res) => {
                addBtn.disabled = false;
                if (!res || !res.success) return;
                // Calls cart-drawer.js's own open/refresh directly rather than
                // going through WooCommerce's shared 'added_to_cart' jQuery
                // event — that event is also wired to WC core's own
                // wc-add-to-cart.js, which would otherwise insert its default
                // (unstyled, and redundant here since the drawer already shows
                // the cart) "View cart" link right after this button.
                openWithFragments(res.data.fragments);
            })
            .catch(() => { addBtn.disabled = false; });
    });
}

// Desktop: the gallery sticks under the header (_product-page.scss) only
// while it fits the screen below it — a taller one would keep its own
// thumbnails out of view.
function initStickyGallery(page) {
    const gallery = page.querySelector('.product-page__gallery');
    const header = document.getElementById('site-header');
    if (!gallery) return;
    const update = () => {
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        gallery.classList.toggle('product-page__gallery--sticky', gallery.offsetHeight + headerHeight + 32 <= window.innerHeight);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('load', update);
}

function init() {
    const page = document.getElementById('product-page');
    if (!page) return;
    initGallery(page);
    initStickyGallery(page);
    initTabs(page);
    initBundle(page);
}

export default { init };
